import { graphql, useStaticQuery } from "gatsby"
import React, { useState, useEffect } from "react"
import FilterBar from "components/FilterBar"
import { SEOHead } from "components/SEO"
import useTutorialAccess from "effects/useTutorialAccess"
import parseYoutubeId from "lib/validateYoutubeUrl"
import Section from "components/Layout/Section"
import SimpleHeader from "components/Layout/SimpleHeader"
import TutorialCard from "components/TutorialCard"
import TutorialModal from "components/TutorialModal"
import Layout from "components/ui/Layout"
import * as css from "components/pages/tutorials/Tutorials.module.css"

const TUTORIAL_FILTERS = [
  { id: null, label: "All" },
  { id: "Feature Highlights", label: "Feature Highlights" },
  { id: "Live Demos", label: "Live Demos" },
]

function TutorialsPage({ location: { pathname, search } }) {
  const [activeCategory, setActiveCategory] = useState("Feature Highlights")
  const [selectedTutorial, setSelectedTutorial] = useState(null)
  const { hasAccess, isLoading, grantAccess } = useTutorialAccess()

  const data = useStaticQuery(graphql`
    {
      allTutorialsYaml(sort: { order: ASC }) {
        nodes {
          id
          title
          slug
          author
          category
          duration
          videoUrl
          youtubeId
          description
          date(formatString: "MMMM D, YYYY")
          thumbnail {
            url
            alt
          }
          order
          relatedVideos
        }
      }
      allEventsYaml(sort: { date: DESC }) {
        nodes {
          id
          title
          slug
          author
          type
          videoUrl
          description
          date
          thumbnail {
            url
            alt
          }
        }
      }
    }
  `)

  // Auto-synthesize Live Demos from past live-demo events that have a YouTube URL
  const today = new Date()
  const liveDemos = data.allEventsYaml.nodes
    .filter(
      (e) =>
        e.type !== "conference" &&
        e.videoUrl &&
        parseYoutubeId(e.videoUrl) &&
        new Date(e.date) < today
    )
    .map((e) => ({
      id: `event-${e.id}`,
      title: e.title,
      slug: e.slug,
      // Events have no structured speaker field today, so this is usually
      // empty — AuthorAvatars renders nothing. Set `author` on an event
      // (e.g. "Jane Doe & John Roe") to surface presenter avatars on its card.
      author: e.author,
      category: "Live Demos",
      youtubeId: parseYoutubeId(e.videoUrl),
      description: e.description,
      date: e.date
        ? new Date(e.date).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })
        : null,
      thumbnail: e.thumbnail,
      relatedVideos: [],
    }))

  const tutorials = [...data.allTutorialsYaml.nodes, ...liveDemos]

  // Deep link: ?watch={slug} opens the matching tutorial modal on load
  useEffect(() => {
    if (!search) return
    const watchSlug = new URLSearchParams(search).get("watch")
    if (!watchSlug) return
    const target = tutorials.find((t) => t.slug === watchSlug)
    if (target) {
      setSelectedTutorial(target)
      setActiveCategory(target.category)
    }
  }, [])

  // Filter by category
  const filteredTutorials = activeCategory
    ? tutorials.filter((t) => t.category === activeCategory)
    : tutorials

  const handleCardClick = (tutorial) => {
    setSelectedTutorial(tutorial)
  }

  const handleCloseModal = () => {
    setSelectedTutorial(null)
  }

  return (
    <Layout
      headerFloating
      trackingName="Tutorials"
      pageContentType="listing-page"
      pageName="tutorials"
      pagePath={pathname}
    >
      <Section className={css.hero}>
        <SimpleHeader
          level="h1"
          label="Learn how to get the most out of Preset"
          title="Tutorials"
          titleSize="xxl"
          dash="horizontal"
        />
      </Section>

      <Section className={css.listingSection}>
        <FilterBar
          filters={TUTORIAL_FILTERS}
          activeFilter={activeCategory}
          onChange={setActiveCategory}
          className={css.categoryFilter}
        />

        {filteredTutorials.length > 0 ? (
          <div className={css.grid}>
            {filteredTutorials.map((tutorial) => (
              <TutorialCard
                key={tutorial.id}
                title={tutorial.title}
                author={tutorial.author}
                category={tutorial.category}
                duration={tutorial.duration}
                thumbnail={tutorial.thumbnail}
                onClick={() => handleCardClick(tutorial)}
              />
            ))}
          </div>
        ) : (
          <div className={css.noResults}>
            <p>No tutorials found in this category.</p>
          </div>
        )}
      </Section>

      {selectedTutorial && (
        <TutorialModal
          tutorial={selectedTutorial}
          allTutorials={tutorials}
          hasAccess={hasAccess}
          isLoading={isLoading}
          onGrantAccess={grantAccess}
          onClose={handleCloseModal}
          onSelectTutorial={setSelectedTutorial}
        />
      )}
    </Layout>
  )
}

export function Head() {
  return (
    <SEOHead
      title="Video Tutorials - Learn Preset and Apache Superset"
      description="Watch free video tutorials to learn how to build dashboards, visualize data, and get the most out of Preset and Apache Superset."
    />
  )
}

export default TutorialsPage
